import React from 'react';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { deDE } from '@mui/x-date-pickers/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormDateInputProps } from './FormInputProps';
import 'dayjs/locale/de';
import { DateTimePicker } from '@mui/x-date-pickers';

/**
 * Date/DateTime picker component with German locale.
 * Uses MUI DatePicker or DateTimePicker based on timePicker prop.
 * Converts dayjs values to Date objects for form state.
 */
export const FormInputDate = ({
  name,
  control,
  label,
  required,
  disabled,
  readOnly,
  datePickerProps,
  textFieldProps,
  timePicker,
  calendarDateString,
}: FormDateInputProps) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <LocalizationProvider
          localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
          dateAdapter={AdapterDayjs}
          adapterLocale="de"
        >
          {timePicker && (
            <DateTimePicker
              label={label}
              value={value ? dayjs(value) : null}
              onChange={newValue => {
                if (newValue && newValue.isValid()) onChange(newValue.toDate());
                else onChange(null);
              }}
              readOnly={readOnly}
              disabled={disabled}
              slotProps={{
                textField: {
                  helperText: error ? error.message : null,
                  error: !!error,
                  fullWidth: true,
                  required,
                  ...textFieldProps,
                },
              }}
              {...datePickerProps}
            />
          )}
          {!timePicker && (
            <DatePicker
              label={label}
              value={value ? dayjs(value) : null}
              onChange={newValue => {
                if (newValue && newValue.isValid()) onChange(calendarDateString ? newValue.format('YYYY-MM-DD') : newValue.toDate());
                else onChange(null);
              }}
              readOnly={readOnly}
              disabled={disabled}
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{
                textField: {
                  helperText: error ? error.message : null,
                  error: !!error,
                  fullWidth: true,
                  required,
                  ...textFieldProps,
                },
              }}
              {...datePickerProps}
            />
          )}
        </LocalizationProvider>
      )}
    />
  );
};

/**
 * Standalone date picker without react-hook-form integration.
 * Useful for non-form contexts; minDate disables selection of earlier calendar days.
 */
export const SimpleDatePicker = ({
  label,
  readOnly,
  disabled,
  value,
  onChange,
  minDate,
}: {
  label: string;
  readOnly?: boolean;
  disabled?: boolean;
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: dayjs.Dayjs;
}) => {
  return (
    <LocalizationProvider
      localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
      dateAdapter={AdapterDayjs}
      adapterLocale="de"
    >
      <DatePicker
        label={label}
        value={value ? dayjs(value) : null}
        onChange={newValue => onChange(newValue ? newValue.toDate() : null)}
        readOnly={readOnly}
        disabled={disabled}
        minDate={minDate}
        views={['year', 'month', 'day']}
        openTo="day"
        slotProps={{
          textField: {
            fullWidth: true,
          },
        }}
      />
    </LocalizationProvider>
  );
};
